/**
 * Visual Blocks Language
 *
 * Copyright 2012 Google Inc.
 * http://blockly.googlecode.com/
 * and 2014 Massachusetts Institute of Technology
 * http://zerorobotics.org/
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @fileoverview Generating C++ for variable blocks.
 *
 * Converted from the legacy Zero Robotics Blockly generator
 * to the modern STEMX365 zrCppGenerator.
 */

import { zrCppGenerator } from "./zrCppGenerator";


// ============================================================
// Helper
// ============================================================

function getVariableName(block, fieldName) {
    const variableId =
        block.getFieldValue(fieldName);

    if (!variableId) {
        return "_";
    }

    const workspace =
        block.workspace;

    if (!workspace) {
        return variableId;
    }

    const variable =
        workspace.getVariableById(variableId);

    return variable
        ? variable.name
        : variableId;
}


// ============================================================
// variables_get
// ============================================================

zrCppGenerator.forBlock["variables_get"] =
    function(block) {

        const code =
            getVariableName(block, "VAR");

        return [
            code,
            this.ORDER_ATOMIC
        ];
    };


// ============================================================
// variables_array_get_pointer
// ============================================================

zrCppGenerator.forBlock["variables_array_get_pointer"] =
    function(block) {

        const code =
            getVariableName(block, "VAR");

        return [
            code,
            this.ORDER_ATOMIC
        ];
    };


// ============================================================
// variables_set
// ============================================================

zrCppGenerator.forBlock["variables_set"] =
    function(block) {

        const argument0 =
            this.valueToCode(
                block,
                "VALUE",
                this.ORDER_ASSIGNMENT
            ) || "0";

        const varName =
            getVariableName(block, "VAR");

        return (
            varName +
            " = " +
            argument0 +
            ";\n"
        );
    };


// ============================================================
// variables_declare
// ============================================================

zrCppGenerator.forBlock["variables_declare"] =
    function(block) {

        const varName =
            getVariableName(block, "NAME");

        const val =
            block.getFieldValue("VALUE") || "0";

        return (
            varName +
            " = " +
            val +
            ";\n"
        );
    };


// ============================================================
// variables_array_get
// ============================================================

zrCppGenerator.forBlock["variables_array_get"] =
    function(block) {

        // Uses 0-based indices.
        // This matches the original Zero Robotics generator.

        const index =
            this.valueToCode(
                block,
                "INDEX",
                this.ORDER_NONE
            ) || "0";

        const array =
            getVariableName(block, "ARRAY") || "_";

        const code =
            array +
            "[" +
            index +
            "]";

        return [
            code,
            this.ORDER_MEMBER
        ];
    };


// ============================================================
// variables_array_set
// ============================================================

zrCppGenerator.forBlock["variables_array_set"] =
    function(block) {

        // Uses 0-based indices.
        // This matches the original Zero Robotics generator.

        const index =
            this.valueToCode(
                block,
                "INDEX",
                this.ORDER_NONE
            ) || "0";

        const array =
            getVariableName(block, "ARRAY") || "_";

        const value =
            this.valueToCode(
                block,
                "VALUE",
                this.ORDER_ASSIGNMENT
            ) || "0";

        return (
            array +
            "[" +
            index +
            "] = " +
            value +
            ";\n"
        );
    };


// ============================================================
// variables_array_declare
// ============================================================

zrCppGenerator.forBlock["variables_array_declare"] =
    function(block) {

        const varName =
            getVariableName(block, "NAME");

        const len =
            block.getFieldValue("LENGTH");

        let code = "";

        for (let i = 0; i < len; i++) {

            const val =
                block.getFieldValue(
                    "VALUE" + i
                ) || "0";

            code +=
                varName +
                "[" +
                i +
                "] = " +
                val +
                ";\n";
        }

        return code;
    };